import { Link } from 'react-router'
import { cn } from '#app/utils/misc.tsx'
import { useOptionalAdminUser } from '#app/utils/user'
import documents from '#content/documents.ts'
import { Icon, ToolTipIcon } from './ui/icon'
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	NavigationSubMenuItem,
	NavigationSubMenuItemHorizontal,
	navigationMenuTriggerStyle,
} from './ui/navigation-menu'
import { Separator } from './ui/separator'

const iconStyle = 'mr-0.5 mt-0.5 w-5 h-5 md:max-2xl:hidden max-md:w-6 max-md:h-6'

function IrrigationNavigationMenu({
	schedules,
}: {
	schedules: {
		signup: { date: string; deadline: string; state: string } | null | undefined
		current: { date: string } | null | undefined
		previous: { date: string } | null | undefined
	}
}) {
	return (
		<NavigationMenu className="flex-none">
			<NavigationMenuList>
				<NavigationMenuItem>
					<NavigationMenuTrigger>
						<Icon className={iconStyle} name="droplets" aria-hidden="true" />
						<p className="max-md:hidden">Irrigation</p>
					</NavigationMenuTrigger>
					<NavigationMenuContent className="hover:z-10">
						<div className="flex w-[300px] flex-col flex-wrap gap-3 p-4 lg:w-[500px]">
							<div id="irrigation-schedules-header" className="w-full">
								<NavigationMenuLink asChild>
									<Link to="/schedules">
										<div className="from-muted/50 to-muted z-1 flex h-full w-full grow flex-col items-center justify-center rounded-md bg-linear-to-b p-6 no-underline outline-none select-none focus:shadow-md">
											<div className="flex flex-row">
												<Icon
													name="droplet"
													className="h-8 w-8"
													aria-hidden="true"
												/>
												<Icon
													name="droplets"
													className="h-8 w-8"
													aria-hidden="true"
												/>
											</div>
											<div className="mt-2 mb-2 text-lg font-medium">
												Irrigation Schedules
											</div>
										</div>
									</Link>
								</NavigationMenuLink>
							</div>
							<Link
								to="/schedule/map"
								className="rounded-md border border-solid bg-sky-800 text-white hover:text-sky-800"
							>
								<NavigationSubMenuItem key="water-location" title="">
									<div className="text-center text-lg font-medium text-white hover:text-sky-800">
										Where is the water?
									</div>
								</NavigationSubMenuItem>
							</Link>
							<div
								id="irrigation-schedules"
								className="flex flex-row flex-wrap gap-2"
							>
								{[schedules?.previous, schedules?.current].map(
									(schedule, index) => (
										<div
											key={`closed-${schedule?.date}`}
											className={`flex w-[265px] flex-col rounded-md border-2 border-solid lg:w-[150px] ${index ? 'border-green-700' : 'border-red-700'}`}
										>
											<div className="text-md bg-secondary mb-2 w-full rounded-t-sm border-b-2 border-solid p-2 text-center font-bold">
												{schedule?.date}
											</div>
											<Link
												to={`/schedule/${schedule?.date}/timeline`}
												className="m-1.5 rounded-md border border-solid"
											>
												<NavigationSubMenuItem key="schedule" title="">
													<div className="text-md my-0.5 font-medium">
														{index ? 'Current Timeline' : 'Previous Timeline'}
													</div>
												</NavigationSubMenuItem>
											</Link>
											<Link
												to={`schedule/${schedule?.date}/crossovers`}
												className="m-1.5 rounded-md border border-solid bg-yellow-700"
											>
												<NavigationSubMenuItem key="acknowledgements-volunteers">
													<div className="text-md my-0.5 font-medium text-white hover:text-yellow-700">
														Gate Changes & Crossovers
													</div>
												</NavigationSubMenuItem>
											</Link>
										</div>
									),
								)}
								<div className="flex w-[265px] flex-col justify-between rounded-md border-2 border-solid border-blue-700 lg:w-[150px]">
									<div className="text-md bg-secondary mb-2 w-full rounded-t-sm border-b-2 border-solid p-2 text-center align-middle font-bold">
										<div className="flex flex-col">
											{schedules?.signup ? `${schedules?.signup.date} ` : ''}
											{schedules?.signup?.state === 'locked' ? (
												<p className="text-destructive">
													This schedule is locked.
												</p>
											) : (
												<>
													<Separator className="my-2" />
													<div>Deadline </div>
													{schedules?.signup?.deadline}
												</>
											)}
										</div>
									</div>
									<Link
										to={`/schedule/${schedules?.signup?.date}/signup`}
										className={`${schedules?.signup ? '' : 'pointer-events-none'} m-1.5 rounded-md border border-solid`}
									>
										<NavigationSubMenuItem key="signup" title="">
											<div className="text-md my-0.5 font-medium">
												{schedules?.signup
													? 'View Sign Up Sheet'
													: 'No Schedules Open'}
											</div>
										</NavigationSubMenuItem>
									</Link>
								</div>
							</div>
						</div>
					</NavigationMenuContent>
				</NavigationMenuItem>
			</NavigationMenuList>
		</NavigationMenu>
	)
}

function DocumentsAdminPanel({ userIsAdmin }: { userIsAdmin: boolean }) {
	return (
		<div className="from-muted/50 to-muted z-1 flex h-full w-full grow flex-col items-center justify-center rounded-md bg-linear-to-b p-6 no-underline outline-none select-none focus:shadow-md">
			<div className="flex flex-row">
				<Icon name="cwf" className="h-24 w-24" aria-hidden="true" />
			</div>
			<div className="mt-2 mb-2 text-xl font-medium">Documents</div>
			{userIsAdmin ? (
				<p className="text-muted-foreground text-sm leading-tight">
					Admin Panel
				</p>
			) : (
				<></>
			)}
		</div>
	)
}

function DocumentsNavigationMenu({ userIsAdmin }: { userIsAdmin: boolean }) {
	return (
		<NavigationMenu className="flex-none">
			<NavigationMenuList>
				<NavigationMenuItem>
					<NavigationMenuTrigger>
						<Icon className={iconStyle} name="file-text" aria-hidden="true" />
						<p className="max-md:hidden">Documents</p>
					</NavigationMenuTrigger>
					<NavigationMenuContent>
						<ol className="grid w-[300px] grid-cols-1 gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-2">
							<li className="row-span-2">
								<NavigationMenuLink asChild>
									{userIsAdmin ? (
										<Link to="/keystatic">
											<DocumentsAdminPanel userIsAdmin={userIsAdmin} />
										</Link>
									) : (
										<DocumentsAdminPanel userIsAdmin={userIsAdmin} />
									)}
								</NavigationMenuLink>
							</li>
							{documents.map((doc) => (
								<NavigationSubMenuItem key={doc.title} {...doc}>
									{doc.description}
								</NavigationSubMenuItem>
							))}
						</ol>
					</NavigationMenuContent>
				</NavigationMenuItem>
			</NavigationMenuList>
		</NavigationMenu>
	)
}

function MeetingsAdminPanel({ userIsAdmin }: { userIsAdmin: boolean }) {
	return (
		<div className="from-muted/50 to-muted z-1 flex h-full w-full grow flex-col items-center justify-center rounded-md bg-linear-to-b p-6 no-underline outline-none select-none focus:shadow-md">
			<div className="flex flex-row">
				<Icon name="calendar" className="h-14 w-14" aria-hidden="true" />
			</div>
			<div className="mt-2 mb-2 text-xl font-medium">Meeting Minutes</div>
			{userIsAdmin ? (
				<p className="text-muted-foreground text-sm leading-tight">
					Admin Panel
				</p>
			) : (
				<></>
			)}
		</div>
	)
}

function MeetingsNavigationMenu({
	meetings,
	userIsAdmin,
}: {
	meetings: { id: string; date: string; executive: boolean }[]
	userIsAdmin: boolean
}) {
	return (
		<NavigationMenu className="flex-none">
			<NavigationMenuList>
				<NavigationMenuItem>
					<NavigationMenuTrigger>
						<Icon className={iconStyle} name="calendar" aria-hidden="true" />
						<p className="max-md:hidden">Meetings</p>
					</NavigationMenuTrigger>
					<NavigationMenuContent>
						<ol className="grid w-[320px] grid-cols-1 gap-3 p-4">
							<li className="row-span-3">
								<NavigationMenuLink asChild>
									{userIsAdmin ? (
										<Link to="/meetings">
											<MeetingsAdminPanel userIsAdmin={userIsAdmin} />
										</Link>
									) : (
										<MeetingsAdminPanel userIsAdmin={userIsAdmin} />
									)}
								</NavigationMenuLink>
							</li>
							{meetings
								.filter(({ executive }) => !executive || userIsAdmin)
								.slice(0, 12)
								.map((meeting) => (
									<NavigationSubMenuItemHorizontal
										key={meeting.id}
										href={`/meetings/${meeting.date}`}
										title={meeting.date}
									>
										{meeting.executive
											? 'Executive Minutes'
											: 'General Minutes'}
									</NavigationSubMenuItemHorizontal>
								))}
						</ol>
					</NavigationMenuContent>
				</NavigationMenuItem>
			</NavigationMenuList>
		</NavigationMenu>
	)
}

function MembersNavigationMenu() {
	return (
		<NavigationMenu className="flex-none">
			<NavigationMenuList>
				<NavigationMenuItem>
					<NavigationMenuTrigger>
						<ToolTipIcon
							className={iconStyle}
							name="users"
							tooltip="Members"
							aria-hidden="true"
						/>
						<p className="max-md:hidden">Members</p>
					</NavigationMenuTrigger>
					<NavigationMenuContent className="hover:z-10">
						<ul className="grid w-[300px] grid-cols-1 gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
							<li className="row-span-3">
								<NavigationMenuLink asChild>
									<Link to="/members">
										<div className="from-muted/50 to-muted z-1 flex h-full w-full flex-col items-center justify-center rounded-md bg-linear-to-b p-6 no-underline outline-none select-none focus:shadow-md">
											<div className="flex flex-row">
												<Icon
													name="user-search"
													className="h-8 w-8"
													aria-hidden="true"
												/>
											</div>
											<div className="mt-2 mb-1 text-lg font-medium">
												Members
											</div>
											<p className="text-muted-foreground text-sm leading-tight">
												Balances
											</p>
										</div>
									</Link>
								</NavigationMenuLink>
							</li>
							<Link to="/members/transactions">
								<NavigationSubMenuItem
									key="members-transactions"
									title="All Transactions"
								>
									<div className="mt-2 mb-1 text-sm font-medium">
										Members' Transactions
									</div>
								</NavigationSubMenuItem>
							</Link>
							<Link to="/stripe/transactions">
								<NavigationSubMenuItem
									key="stripe-transactions"
									title="Stripe Transactions"
								>
									<div className="mt-2 mb-1 text-sm font-medium">
										Stripe Payments
									</div>
								</NavigationSubMenuItem>
							</Link>
							<Link to="/members/contacts">
								<NavigationSubMenuItem
									key="members-contacts"
									title="Contact List"
								>
									<div className="mt-2 mb-1 text-sm font-medium">
										Members' Contact Information
									</div>
								</NavigationSubMenuItem>
							</Link>
							<Link to="/admin/audit">
								<NavigationSubMenuItem key="admin-audit" title="Audit Log">
									<div className="mt-2 mb-1 text-sm font-medium">
										Member and email changes
									</div>
								</NavigationSubMenuItem>
							</Link>
							<Link to="/members/restrictions">
								<NavigationSubMenuItem
									key="members-restrictions"
									title="Restrictions"
								>
									<div className="mt-2 mb-1 text-sm font-medium">
										Restriction Information
									</div>
								</NavigationSubMenuItem>
							</Link>
						</ul>
					</NavigationMenuContent>
				</NavigationMenuItem>
			</NavigationMenuList>
		</NavigationMenu>
	)
}

export function MainNavigationMenu({
	schedules,
	meetings,
}: {
	schedules: {
		signup: { date: string; deadline: string; state: string } | null | undefined
		current: { date: string } | null | undefined
		previous: { date: string } | null | undefined
	}
	meetings: { id: string; date: string; executive: boolean }[]
}) {
	const userIsAdmin = useOptionalAdminUser() ?? false

	return (
		<NavigationMenu>
			<NavigationMenuList>
				<NavigationMenuItem>
					<Link to="/">
						<NavigationMenuLink className={navigationMenuTriggerStyle()}>
							<ToolTipIcon
								className={iconStyle}
								name="home"
								tooltip="Home"
								aria-hidden="true"
							/>
							<p className="max-md:hidden">Home</p>
						</NavigationMenuLink>
					</Link>
				</NavigationMenuItem>
				<IrrigationNavigationMenu schedules={schedules} />
				<DocumentsNavigationMenu userIsAdmin={userIsAdmin} />
				<MeetingsNavigationMenu meetings={meetings} userIsAdmin={userIsAdmin} />
				{userIsAdmin ? <MembersNavigationMenu /> : null}
				<NavigationMenuItem>
					<Link to="/resources">
						<NavigationMenuLink className={navigationMenuTriggerStyle()}>
							<ToolTipIcon
								className={iconStyle}
								name="id-card"
								tooltip="Resources"
								aria-hidden="true"
							/>
							<p className="text-nowrap text-ellipsis max-md:hidden">
								Resources
							</p>
						</NavigationMenuLink>
					</Link>
				</NavigationMenuItem>
				<NavigationMenuItem>
					<Link to="/contact-us">
						<NavigationMenuLink className={navigationMenuTriggerStyle()}>
							<ToolTipIcon
								className={iconStyle}
								name="envelope-closed"
								tooltip="Contact Us"
								aria-hidden="true"
							/>
							<p className="text-nowrap text-ellipsis max-md:hidden">
								Contact Us
							</p>
						</NavigationMenuLink>
					</Link>
				</NavigationMenuItem>
			</NavigationMenuList>
			<NavigationMenuItem>
				<Link to="/feedback">
					<NavigationMenuLink
						className={cn(
							navigationMenuTriggerStyle(),
							'bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white focus:bg-emerald-700 focus:text-white',
						)}
					>
						<span className="relative mr-1 flex h-2.5 w-2.5">
							<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-200 opacity-75" />
							<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
						</span>
						<ToolTipIcon
							className={iconStyle}
							name="sms"
							tooltip="Share Feedback"
							aria-hidden="true"
						/>
						<p className="text-nowrap text-ellipsis max-md:hidden">Feedback</p>
					</NavigationMenuLink>
				</Link>
			</NavigationMenuItem>
		</NavigationMenu>
	)
}
